import { FC } from 'react';

import { PopperPortal, Tag } from '@components/index';

import LabelsFilterButton from './LabelFilterButton';
import LabelsPopper from './LabelsPopper';
import { LabelItem, LabelList } from './types';

type Props = {
  selectedLabelList: LabelList;
  setSelectedLabelList: React.Dispatch<React.SetStateAction<LabelList>>;
  updateSelectedLabels?: (selectedLabelIds: LabelList) => void;
  isFilterMode?: boolean;
  isAssetInherited?: boolean;
  isAssetLocked?: boolean;
};

const LabelSelection: FC<Props> = ({
  selectedLabelList,
  setSelectedLabelList,
  updateSelectedLabels,
  isFilterMode = false,
  isAssetInherited = false,
  isAssetLocked = false,
}) => {
  const selectedLabelCount = Object.keys(selectedLabelList).length;

  const onSelectOption = (option: LabelItem) => {
    // Prevent selection of inherited labels in non-filter mode only if asset is also inherited
    if (!isFilterMode && option.parentId && isAssetInherited) {
      return;
    }

    setSelectedLabelList((prev: LabelList) => {
      const updatedSelection = { ...prev };
      if (updatedSelection[option.id]) {
        delete updatedSelection[option.id];
      } else {
        updatedSelection[option.id] = option;
      }

      updateSelectedLabels?.(updatedSelection);

      return updatedSelection;
    });
  };

  const RefComponent = () => {
    if (isFilterMode) {
      return <LabelsFilterButton count={selectedLabelCount} />;
    }

    return <Tag count={selectedLabelCount} />;
  };

  return (
    <PopperPortal
      placement={isFilterMode ? 'bottom-end' : 'right-end'}
      popComponent={
        <LabelsPopper
          selectedOptions={selectedLabelList}
          onSelectOption={onSelectOption}
          onClearAllOptions={() => {
            setSelectedLabelList({});
            updateSelectedLabels?.({});
          }}
          onLabelEditSuccess={(data, label) => {
            if (selectedLabelList?.[label.id]) {
              setSelectedLabelList((prev: LabelList) => {
                return {
                  ...prev,
                  [label.id]: data as any,
                };
              });
            }
          }}
          onLabelDeleteSuccess={label => {
            if (selectedLabelList?.[label.id]) {
              setSelectedLabelList((prev: LabelList) => {
                const updatedSelection = { ...prev };
                delete updatedSelection[label.id];
                updateSelectedLabels?.(updatedSelection);

                return updatedSelection;
              });
            }
          }}
          isFilterMode={isFilterMode}
          isAssetInherited={isAssetInherited}
          isAssetLocked={isAssetLocked}
        />
      }
      refComponent={<RefComponent />}
      isKeepPopperOpenAfterClick={true}
    />
  );
};

export default LabelSelection;
